import { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Container,
  Box,
  Typography,
  Stack,
  Alert,
  CircularProgress,
  Button,
  Snackbar,
  AlertTitle,
  IconButton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import CloudIcon from '@mui/icons-material/Cloud';
import SyncIcon from '@mui/icons-material/Sync';
import CloudOffIcon from '@mui/icons-material/CloudOff';
import RefreshIcon from '@mui/icons-material/Refresh';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import FolderSelectionDialog from '../components/FolderSelectionDialog';
import DocumentManager from '../components/DocumentManager';
import FileUpload from '../components/FileUpload';
import { API_ENDPOINTS, type ApiError, ApiErrorCode } from '../config/api';
import { getErrorDetails } from '../utils/logger';

interface ContextDocument {
  id: string;
  path: string;
  category: 'example' | 'capability' | 'template' | 'imported';
  metadata: {
    title: string;
    tags: string[];
    summary: string;
    folderPath?: string;
    modifiedTime?: string;
  };
}

interface ContextData {
  documents: ContextDocument[];
  totalCount: number;
}

interface DriveStatus {
  connected: boolean;
  folderId?: string;
  folderName?: string;
  lastSyncedAt?: string;
}


export default function ContextViewer() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<ApiError | null>(null);
  const [contextData, setContextData] = useState<ContextData | null>(null);
  const [errorCopied, setErrorCopied] = useState(false);

  // Google Drive state
  const [driveStatus, setDriveStatus] = useState<DriveStatus>({ connected: false });
  const [showFolderDialog, setShowFolderDialog] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success'
  });

  // Accordion state
  const [driveAccordionExpanded, setDriveAccordionExpanded] = useState(true);
  const [uploadAccordionExpanded, setUploadAccordionExpanded] = useState(true);

  // Calculate imported documents
  const importedDocuments = contextData?.documents.filter(d => d.category === 'imported') || [];

  useEffect(() => {
    fetchContextData();
    fetchDriveStatus();

    // Check URL params for Drive connection status
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('driveConnected') === 'true') {
      setSnackbar({ open: true, message: 'Google Drive connected successfully!', severity: 'success' });
      window.history.replaceState({}, '', '/context');
    } else if (urlParams.get('driveError') === 'true') {
      setSnackbar({ open: true, message: 'Failed to connect Google Drive', severity: 'error' });
      window.history.replaceState({}, '', '/context');
    }
  }, []);

  // Auto-collapse accordions when user has documents
  useEffect(() => {
    const hasDocuments = importedDocuments.length > 0;
    if (hasDocuments) {
      setDriveAccordionExpanded(false);
      setUploadAccordionExpanded(false);
    }
  }, [importedDocuments.length]); // Run when document count changes

  const fetchContextData = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await axios.get(API_ENDPOINTS.context.list, { withCredentials: true });
      setContextData(response.data);
    } catch (err) {
      const apiError = err as ApiError;
      setError(apiError);
    } finally {
      setLoading(false);
    }
  };

  const handleCopyError = () => {
    if (!error) return;
    const details = getErrorDetails(error.message, {
      code: error.code,
      details: error.details,
      status: error.status,
    });
    navigator.clipboard.writeText(details).then(() => {
      setErrorCopied(true);
      setTimeout(() => setErrorCopied(false), 3000);
    });
  };

  const fetchDriveStatus = async () => {
    try {
      const response = await axios.get(API_ENDPOINTS.drive.status, { withCredentials: true });
      setDriveStatus(response.data);
    } catch (err) {
      console.error('Failed to fetch Drive status:', err);
    }
  };

  const handleConnectDrive = async () => {
    try {
      const response = await axios.get(API_ENDPOINTS.drive.auth, { withCredentials: true });
      window.location.href = response.data.authUrl;
    } catch (err) {
      console.error('Failed to initiate Drive auth:', err);
      setSnackbar({ open: true, message: 'Failed to connect to Google Drive', severity: 'error' });
    }
  };

  const handleSelectFolder = () => {
    setShowFolderDialog(true);
  };

  const handleFolderSelected = async (folderId: string, includeSubfolders: boolean) => {
    try {
      await axios.post(API_ENDPOINTS.drive.selectFolder, { folderId }, { withCredentials: true });
      setShowFolderDialog(false);

      // Auto-sync after folder selection
      setSyncing(true);
      const response = await axios.post(
        API_ENDPOINTS.drive.sync,
        { includeSubfolders },
        { withCredentials: true }
      );

      await fetchDriveStatus();
      await fetchContextData();

      setSnackbar({
        open: true,
        message: `Folder selected and synced! Processed ${response.data.result.filesProcessed} files`,
        severity: 'success'
      });
    } catch (err) {
      console.error('Failed to select folder and sync:', err);
      setSnackbar({ open: true, message: 'Failed to select folder and sync', severity: 'error' });
    } finally {
      setSyncing(false);
    }
  };

  const handleSync = async () => {
    try {
      setSyncing(true);
      const response = await axios.post(
        API_ENDPOINTS.drive.sync,
        { includeSubfolders: true },
        { withCredentials: true }
      );
      await fetchDriveStatus();
      await fetchContextData();
      setSnackbar({
        open: true,
        message: `Sync complete! Processed ${response.data.result.filesProcessed} files`,
        severity: 'success'
      });
    } catch (err) {
      console.error('Sync failed:', err);
      setSnackbar({ open: true, message: 'Sync failed', severity: 'error' });
    } finally {
      setSyncing(false);
    }
  };

  const handleDisconnect = async () => {
    try {
      await axios.post(API_ENDPOINTS.drive.disconnect, {}, { withCredentials: true });
      await fetchDriveStatus();
      await fetchContextData();
      setSnackbar({ open: true, message: 'Google Drive disconnected', severity: 'success' });
    } catch (err) {
      console.error('Failed to disconnect:', err);
      setSnackbar({ open: true, message: 'Failed to disconnect', severity: 'error' });
    }
  };

  const handleDocumentDeleted = async () => {
    await fetchContextData();
    setSnackbar({ open: true, message: 'Document deleted successfully', severity: 'success' });
  };

  const handleReindex = async () => {
    try {
      setSyncing(true);
      await axios.post(API_ENDPOINTS.context.reindex, {}, { withCredentials: true });
      await fetchContextData();
      setSnackbar({ open: true, message: 'Documents re-indexed successfully', severity: 'success' });
    } catch (err) {
      console.error('Failed to reindex:', err);
      setSnackbar({ open: true, message: 'Failed to reindex documents', severity: 'error' });
    } finally {
      setSyncing(false);
    }
  };

  if (loading) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error) {
    const isConfigIssue = error.code === ApiErrorCode.CORS_ERROR || error.code === ApiErrorCode.NETWORK_ERROR;

    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          action={
            <IconButton
              color="inherit"
              size="small"
              onClick={handleCopyError}
              title="Copy error details"
            >
              <ContentCopyIcon fontSize="small" />
            </IconButton>
          }
        >
          <AlertTitle>
            {error.message}
          </AlertTitle>
          {error.details && (
            <Typography variant="body2" sx={{ mt: 1 }}>
              {error.details}
            </Typography>
          )}
          {isConfigIssue && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" fontWeight={600}>
                Possible fixes:
              </Typography>
              <Typography variant="body2" component="ul" sx={{ mt: 0.5, pl: 2 }}>
                <li>Ensure VITE_API_URL is set in Vercel environment variables</li>
                <li>Ensure FRONTEND_URL is set in Railway environment variables</li>
                <li>Check that both frontend and backend are deployed and running</li>
              </Typography>
            </Box>
          )}
          {error.code && (
            <Typography variant="caption" display="block" sx={{ mt: 1, opacity: 0.7 }}>
              Error Code: {error.code}
              {error.status && ` (HTTP ${error.status})`}
              {errorCopied && ' • Copied!'}
            </Typography>
          )}
        </Alert>
        <Button
          variant="outlined"
          onClick={() => fetchContextData()}
          startIcon={<RefreshIcon />}
        >
          Retry
        </Button>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Box mb={4}>
        <Typography variant="h4" component="h1" gutterBottom fontWeight={600}>
          Context Documents
        </Typography>
        <Typography variant="body1" color="text.secondary">
          View all indexed documents that Claude uses to generate proposals
        </Typography>
      </Box>

      {/* Google Drive Integration Accordion */}
      <Accordion
        expanded={driveAccordionExpanded}
        onChange={(_, isExpanded) => setDriveAccordionExpanded(isExpanded)}
        sx={{ mb: 2 }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          sx={{
            '& .MuiAccordionSummary-content': {
              alignItems: 'center',
              gap: 2
            }
          }}
        >
          <CloudIcon color={driveStatus.connected ? 'primary' : 'disabled'} />
          <Box>
            <Typography variant="h6" fontWeight={600}>
              Google Drive Integration
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {driveStatus.connected
                ? `Connected to: ${driveStatus.folderName || 'No folder selected'}`
                : 'Connect your Google Drive to sync documents'}
            </Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Stack spacing={2}>
            {driveStatus.lastSyncedAt && (
              <Typography variant="caption" color="text.secondary">
                Last synced: {new Date(driveStatus.lastSyncedAt).toLocaleString()}
              </Typography>
            )}
            <Stack direction="row" spacing={2}>
              {!driveStatus.connected ? (
                <Button
                  variant="contained"
                  startIcon={<CloudIcon />}
                  onClick={handleConnectDrive}
                >
                  Connect Google Drive
                </Button>
              ) : (
                <>
                  <Button
                    variant="outlined"
                    onClick={handleSelectFolder}
                    disabled={syncing}
                  >
                    {driveStatus.folderId ? 'Change Folder' : 'Select Folder'}
                  </Button>
                  <Button
                    variant="contained"
                    startIcon={syncing ? <CircularProgress size={20} /> : <SyncIcon />}
                    onClick={handleSync}
                    disabled={!driveStatus.folderId || syncing}
                  >
                    {syncing ? 'Syncing...' : 'Sync Now'}
                  </Button>
                  <Button
                    variant="outlined"
                    color="error"
                    startIcon={<CloudOffIcon />}
                    onClick={handleDisconnect}
                    disabled={syncing}
                  >
                    Disconnect
                  </Button>
                </>
              )}
            </Stack>
          </Stack>
        </AccordionDetails>
      </Accordion>

      {/* Direct File Upload Accordion */}
      <Accordion
        expanded={uploadAccordionExpanded}
        onChange={(_, isExpanded) => setUploadAccordionExpanded(isExpanded)}
        sx={{ mb: 4 }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          sx={{
            '& .MuiAccordionSummary-content': {
              alignItems: 'center',
              gap: 2
            }
          }}
        >
          <UploadFileIcon color="action" />
          <Box>
            <Typography variant="h6" fontWeight={600}>
              Direct File Upload
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Upload files or folders directly from your computer. These will be combined with your Google Drive documents for proposal generation.
            </Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <FileUpload onUploadComplete={fetchContextData} />
        </AccordionDetails>
      </Accordion>

      {importedDocuments.length > 0 && (
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography variant="body2" color="text.secondary">
              {driveStatus.connected && driveStatus.folderName
                ? `Documents from Google Drive and Direct Uploads`
                : 'Documents from Direct Uploads'}
            </Typography>
          </Stack>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={handleReindex}
            disabled={syncing}
            size="small"
          >
            {syncing ? 'Re-indexing...' : 'Re-index Documents'}
          </Button>
        </Stack>
      )}

      {importedDocuments.length > 0 ? (
        <DocumentManager documents={importedDocuments} onDocumentDeleted={handleDocumentDeleted} />
      ) : (
        <Alert severity="info">
          No documents have been imported yet. Connect your Google Drive or upload files directly to get started.
        </Alert>
      )}

      {/* Folder Selection Dialog */}
      <FolderSelectionDialog
        open={showFolderDialog}
        onClose={() => setShowFolderDialog(false)}
        onFolderSelected={handleFolderSelected}
      />

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
}
